import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import './GanttTable.css';

const GanttTable = ({ data, onItemClick, onDataSourceChange, currentDataSource }) => {
    const [expandedOrders, setExpandedOrders] = useState({});
    const [expandedOperations, setExpandedOperations] = useState({});
    const [timeRange, setTimeRange] = useState(null);
    const [columns, setColumns] = useState([]);
    const [timelineWidth, setTimelineWidth] = useState(1200);
    const [scrollTop, setScrollTop] = useState(0);

    const timelineRef = useRef(null);
    const leftPanelRef = useRef(null);
    const rightPanelRef = useRef(null);

    const DAY_WIDTH = 40;
    const DAY_HEIGHT = 60;

    // Сортировка заказов по свежести
    const sortedData = useMemo(() => {
        if (!data) return [];

        return [...data].sort((a, b) => {
            const dateA = a.startDate ? new Date(a.startDate).getTime() : 0;
            const dateB = b.startDate ? new Date(b.startDate).getTime() : 0;
            return dateB - dateA;
        });
    }, [data]);

    // Расчет временного диапазона
    const calculatedTimeRange = useMemo(() => {
        if (!sortedData || sortedData.length === 0) {
            const today = new Date();
            const start = new Date(today);
            start.setMonth(start.getMonth() - 1);
            start.setHours(0, 0, 0, 0);

            const end = new Date(today);
            end.setMonth(end.getMonth() + 3);
            end.setHours(23, 59, 59, 999);

            return { start, end };
        }

        let minDate = null;
        let maxDate = null;

        const extractDates = (item) => {
            if (item.startDate) {
                const start = new Date(item.startDate);
                start.setHours(0, 0, 0, 0);
                if (!minDate || start < minDate) minDate = start;
            }
            if (item.endDate) {
                const end = new Date(item.endDate);
                end.setHours(23, 59, 59, 999);
                if (!maxDate || end > maxDate) maxDate = end;
            }
        };

        sortedData.forEach(order => {
            extractDates(order);
            order.operations?.forEach(operation => {
                extractDates(operation);
                operation.resources?.forEach(resource => {
                    extractDates(resource);
                });
            });
        });

        if (minDate && maxDate) {
            // Расширяем диапазон на 2 недели в обе стороны
            const earliestStart = new Date(minDate);
            earliestStart.setDate(earliestStart.getDate() - 14);

            const latestEnd = new Date(maxDate);
            latestEnd.setDate(latestEnd.getDate() + 14);

            return { start: earliestStart, end: latestEnd };
        }

        const today = new Date();
        const start = new Date(today);
        start.setMonth(start.getMonth() - 1);
        start.setHours(0, 0, 0, 0);

        const end = new Date(today);
        end.setMonth(end.getMonth() + 3);
        end.setHours(23, 59, 59, 999);

        return { start, end };
    }, [sortedData]);

    const displayData = useMemo(() => {
        return sortedData ? sortedData.slice(0, 50) : [];
    }, [sortedData]);

    // Высота контента
    const totalRowCount = useMemo(() => {
        let count = 0;
        displayData.forEach(order => {
            count++;
            if (expandedOrders[order.id] && order.operations) {
                order.operations.forEach(operation => {
                    count++;
                    if (expandedOperations[`${order.id}-${operation.id}`] && operation.resources) {
                        count += operation.resources.length;
                    }
                });
            }
        });
        return count;
    }, [displayData, expandedOrders, expandedOperations]);

    const timelineContentHeight = totalRowCount * DAY_HEIGHT;

    // Расчет колонок дат
    useEffect(() => {
        if (timeRange?.start && timeRange?.end) {
            const newColumns = [];
            const current = new Date(timeRange.start);
            const end = new Date(timeRange.end);

            current.setHours(0, 0, 0, 0);
            end.setHours(0, 0, 0, 0);

            while (current <= end) {
                newColumns.push(new Date(current));
                current.setDate(current.getDate() + 1);
            }

            setColumns(newColumns);
            const calculatedWidth = Math.max(newColumns.length * DAY_WIDTH, 1200);
            setTimelineWidth(calculatedWidth);
        }
    }, [timeRange]);

    // Установка временного диапазона
    useEffect(() => {
        if (calculatedTimeRange) {
            setTimeRange(calculatedTimeRange);
        }
    }, [calculatedTimeRange]);

    // Автопрокрутка к текущей дате
    useEffect(() => {
        if (columns.length > 0 && timelineRef.current) {
            const today = new Date();
            today.setHours(0, 0, 0, 0);

            let todayIndex = -1;
            columns.forEach((date, index) => {
                const compareDate = new Date(date);
                compareDate.setHours(0, 0, 0, 0);
                if (compareDate.getTime() === today.getTime()) {
                    todayIndex = index;
                }
            });

            if (todayIndex !== -1) {
                const targetScroll = Math.max(0, (todayIndex * DAY_WIDTH) - 400);

                setTimeout(() => {
                    if (timelineRef.current) {
                        timelineRef.current.scrollLeft = targetScroll;
                    }
                }, 100);
            }
        }
    }, [columns]);

    // Синхронизация вертикальной прокрутки
    useEffect(() => {
        const handleScroll = (e) => {
            const scrollTop = e.target.scrollTop;
            setScrollTop(scrollTop);

            // Синхронизируем обе панели
            if (e.target === rightPanelRef.current) {
                if (leftPanelRef.current) {
                    leftPanelRef.current.scrollTop = scrollTop;
                }
            } else if (e.target === leftPanelRef.current) {
                if (rightPanelRef.current) {
                    rightPanelRef.current.scrollTop = scrollTop;
                }
            }
        };

        const leftPanel = leftPanelRef.current;
        const rightPanel = rightPanelRef.current;

        if (leftPanel && rightPanel) {
            leftPanel.addEventListener('scroll', handleScroll);
            rightPanel.addEventListener('scroll', handleScroll);

            return () => {
                leftPanel.removeEventListener('scroll', handleScroll);
                rightPanel.removeEventListener('scroll', handleScroll);
            };
        }
    }, []);

    // Устанавливаем высоту контейнеров
    useEffect(() => {
        if (leftPanelRef.current && rightPanelRef.current) {
            const containerHeight = Math.max(timelineContentHeight, 600);

            // Устанавливаем высоту контента для вертикальной прокрутки
            const leftContent = leftPanelRef.current.querySelector('.gt-left-content');
            const rightContent = rightPanelRef.current;

            if (leftContent) {
                leftContent.style.height = `${containerHeight}px`;
            }

            if (rightContent) {
                rightContent.style.height = `${containerHeight}px`;
            }
        }
    }, [timelineContentHeight]);

    const toggleOrderExpand = (orderId, event) => {
        event.stopPropagation();
        setExpandedOrders(prev => ({
            ...prev,
            [orderId]: !prev[orderId]
        }));
    };

    const toggleOperationExpand = (operationId, event) => {
        event.stopPropagation();
        setExpandedOperations(prev => ({
            ...prev,
            [operationId]: !prev[operationId]
        }));
    };

    const getStatusColor = (status) => {
        const statusMap = {
            1: '#10b981', 2: '#6b7280', 3: '#f59e0b', 4: '#ef4444',
            5: '#eab308', 6: '#3b82f6', 7: '#8b5cf6', 10: '#10b981'
        };
        return statusMap[status] || '#6b7280';
    };

    const getStatusText = (status) => {
        const statusMap = {
            1: 'Утверждено', 2: 'Завершено', 3: 'Забронировано', 4: 'Отменено',
            5: 'Готов к выполнению', 6: 'В работе', 7: 'На рассмотрении', 10: 'Утверждено'
        };
        return statusMap[status] || 'Неизвестно';
    };

    const calculateTaskPosition = (startDate, endDate) => {
        if (!columns.length || !startDate || !endDate) {
            return { left: 0, width: 0, isVisible: false };
        }

        try {
            const taskStart = new Date(startDate);
            const taskEnd = new Date(endDate);

            taskStart.setHours(0, 0, 0, 0);
            taskEnd.setHours(23, 59, 59, 999);

            let startIndex = -1;
            let endIndex = -1;

            columns.forEach((columnDate, index) => {
                const columnDay = new Date(columnDate);
                columnDay.setHours(0, 0, 0, 0);

                const columnTimestamp = columnDay.getTime();
                const taskStartTimestamp = taskStart.getTime();
                const taskEndTimestamp = taskEnd.getTime();

                if (columnTimestamp >= taskStartTimestamp && columnTimestamp <= taskEndTimestamp) {
                    if (startIndex === -1) {
                        startIndex = index;
                    }
                    endIndex = index;
                }
            });

            if (startIndex === -1 || endIndex === -1) {
                return { left: 0, width: 0, isVisible: false };
            }

            const left = startIndex * DAY_WIDTH;
            const width = Math.max((endIndex - startIndex + 1) * DAY_WIDTH, DAY_WIDTH);

            return {
                left: Math.max(0, left),
                width: width,
                isVisible: true,
                startIndex,
                endIndex
            };

        } catch (error) {
            console.error('Ошибка при расчете позиции:', error);
            return { left: 0, width: 0, isVisible: false };
        }
    };

    // Расчет позиций всех строк
    const rowPositions = useMemo(() => {
        const positions = [];
        let currentRow = 0;

        displayData.forEach(order => {
            // Позиция заказа
            positions.push({
                type: 'order',
                order,
                top: currentRow * DAY_HEIGHT,
                rowIndex: currentRow
            });
            currentRow++;

            // Позиции операций (если развернуто)
            if (expandedOrders[order.id] && order.operations) {
                order.operations.forEach(operation => {
                    positions.push({
                        type: 'operation',
                        order,
                        operation,
                        top: currentRow * DAY_HEIGHT,
                        rowIndex: currentRow
                    });
                    currentRow++;

                    // Позиции ресурсов (если развернуто)
                    if (expandedOperations[`${order.id}-${operation.id}`] && operation.resources) {
                        operation.resources.forEach(resource => {
                            positions.push({
                                type: 'resource',
                                order,
                                operation,
                                resource,
                                top: currentRow * DAY_HEIGHT,
                                rowIndex: currentRow
                            });
                            currentRow++;
                        });
                    }
                });
            }
        });

        return positions;
    }, [displayData, expandedOrders, expandedOperations]);

    // Функция для обрезания длинного текста
    const truncateText = (text, maxLength = 40) => {
        if (!text) return '';
        if (text.length <= maxLength) return text;
        return text.substring(0, maxLength) + '...';
    };

    // Рендер грида строк
    const renderRowGrid = () => {
        return (
            <div className="gt-row-grid" style={{
                width: `${timelineWidth}px`,
                height: `${timelineContentHeight}px`
            }}>
                {Array.from({ length: totalRowCount }).map((_, index) => (
                    <div
                        key={`grid-row-${index}`}
                        className="gt-grid-row"
                        style={{
                            top: `${index * DAY_HEIGHT}px`
                        }}
                    />
                ))}
            </div>
        );
    };

    // Рендер сетки дат
    const renderDateGrid = () => {
        return (
            <div className="gt-date-grid" style={{
                width: `${timelineWidth}px`,
                height: `${timelineContentHeight}px`
            }}>
                {columns.map((date, index) => (
                    <div
                        key={`grid-col-${index}`}
                        className="gt-grid-cell"
                        style={{
                            left: `${index * DAY_WIDTH}px`,
                            width: `${DAY_WIDTH}px`
                        }}
                    />
                ))}
            </div>
        );
    };

    // Рендер календаря
    const renderCalendar = () => {
        if (!columns.length) return null;

        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const isToday = (date) => {
            const compareDate = new Date(date);
            compareDate.setHours(0, 0, 0, 0);
            return compareDate.getTime() === today.getTime();
        };

        const isWeekend = (date) => {
            const dayOfWeek = date.getDay();
            return dayOfWeek === 0 || dayOfWeek === 6;
        };

        // Группировка по месяцам для заголовков
        const monthHeaders = [];
        if (columns.length > 0) {
            let currentMonth = null;
            let startIndex = 0;

            columns.forEach((date, index) => {
                const monthKey = date.getFullYear() + '-' + date.getMonth();

                if (monthKey !== currentMonth) {
                    if (currentMonth !== null) {
                        monthHeaders.push({
                            month: new Date(columns[startIndex]),
                            startIndex,
                            endIndex: index - 1,
                            daysCount: index - startIndex
                        });
                    }
                    currentMonth = monthKey;
                    startIndex = index;
                }
            });

            if (currentMonth !== null) {
                monthHeaders.push({
                    month: new Date(columns[startIndex]),
                    startIndex,
                    endIndex: columns.length - 1,
                    daysCount: columns.length - startIndex
                });
            }
        }

        return (
            <div className="gt-calendar">
                {/* Заголовки месяцев */}
                <div className="gt-month-headers">
                    {monthHeaders.map((header, index) => (
                        <div
                            key={index}
                            className="gt-month-header"
                            style={{
                                width: `${header.daysCount * DAY_WIDTH}px`,
                                minWidth: `${header.daysCount * DAY_WIDTH}px`
                            }}
                            title={header.month.toLocaleDateString('ru-RU', {
                                month: 'long',
                                year: 'numeric'
                            })}
                        >
                            {truncateText(header.month.toLocaleDateString('ru-RU', {
                                month: 'long',
                                year: 'numeric'
                            }), 20)}
                        </div>
                    ))}
                </div>

                {/* Заголовки дней */}
                <div className="gt-day-headers">
                    {columns.map((date, index) => (
                        <div
                            key={index}
                            className={`gt-day-header ${isWeekend(date) ? 'gt-weekend' : ''} ${isToday(date) ? 'gt-today' : ''}`}
                            style={{
                                width: `${DAY_WIDTH}px`,
                                minWidth: `${DAY_WIDTH}px`
                            }}
                            title={date.toLocaleDateString('ru-RU', {
                                weekday: 'long',
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric'
                            })}
                        >
                            <div className="gt-day-of-week">
                                {date.toLocaleDateString('ru-RU', {
                                    weekday: 'short'
                                }).toLowerCase()}
                            </div>
                            <div className="gt-date-number">{date.getDate()}</div>
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    // Рендер текущего дня
    const renderTodayLine = () => {
        if (!columns.length) return null;

        const today = new Date();
        today.setHours(0, 0, 0, 0);

        let todayIndex = -1;
        columns.forEach((date, index) => {
            const compareDate = new Date(date);
            compareDate.setHours(0, 0, 0, 0);
            if (compareDate.getTime() === today.getTime()) {
                todayIndex = index;
            }
        });

        if (todayIndex === -1) return null;

        return (
            <div
                className="gt-today-line"
                style={{
                    left: `${todayIndex * DAY_WIDTH}px`,
                    top: '0',
                    height: `${timelineContentHeight}px`
                }}
            />
        );
    };

    // Рендер всех временных полосок
    const renderTasks = () => {
        return rowPositions.map((position, index) => {
            let taskProps = null;
            let taskType = '';
            let taskData = null;

            switch (position.type) {
                case 'order':
                    taskProps = calculateTaskPosition(position.order.startDate, position.order.endDate);
                    taskType = 'gt-task-order';
                    taskData = position.order;
                    break;
                case 'operation':
                    taskProps = calculateTaskPosition(position.operation.startDate, position.operation.endDate);
                    taskType = 'gt-task-operation';
                    taskData = position.operation;
                    break;
                case 'resource':
                    taskProps = calculateTaskPosition(position.resource.startDate, position.resource.endDate);
                    taskType = 'gt-task-resource';
                    taskData = position.resource;
                    break;
                default:
                    return null;
            }

            if (!taskProps.isVisible) return null;

            const displayName = taskData.name || taskData.orderNumber || taskData.operationNumber || 'Без названия';
            const truncatedName = truncateText(displayName, 30);

            return (
                <div
                    key={`task-${index}`}
                    className={`gt-task ${taskType} gt-status-${taskData.status}`}
                    style={{
                        left: `${taskProps.left}px`,
                        width: `${taskProps.width}px`,
                        top: `${position.top}px`,
                        backgroundColor: getStatusColor(taskData.status)
                    }}
                    onClick={() => onItemClick?.(taskData)}
                    title={`${displayName}\n${new Date(taskData.startDate).toLocaleDateString('ru-RU')} - ${new Date(taskData.endDate).toLocaleDateString('ru-RU')}\nСтатус: ${getStatusText(taskData.status)}`}
                >
                    {taskProps.width > 60 && (
                        <div className="gt-task-content">
                            <span className="gt-task-text" title={displayName}>
                                {truncatedName}
                            </span>
                        </div>
                    )}
                </div>
            );
        });
    };

    // Рендер строк для левой панели
    const renderLeftPanelRows = () => {
        return rowPositions.map((position, index) => {
            const isOrder = position.type === 'order';
            const isOperation = position.type === 'operation';
            const isResource = position.type === 'resource';

            const itemData = isOrder ? position.order : isOperation ? position.operation : position.resource;
            const fullName = itemData.name || itemData.orderNumber || itemData.operationNumber || 'Без названия';
            const truncatedName = truncateText(fullName, 35);

            const statusText = getStatusText(itemData.status);
            const truncatedStatus = truncateText(statusText, 15);

            let metaInfo = '';
            if (isOrder) {
                const dates = itemData.startDate ?
                    `${new Date(itemData.startDate).toLocaleDateString('ru-RU')}${itemData.endDate ? ` - ${new Date(itemData.endDate).toLocaleDateString('ru-RU')}` : ''}` :
                    'Даты не указаны';
                metaInfo = `ID: ${itemData.id} | ${dates}`;
            } else if (isOperation) {
                metaInfo = `Операция: ${itemData.operationNumber || itemData.id}`;
            } else {
                metaInfo = `${itemData.id} | ${itemData.type || 'Тип не указан'}`;
            }

            const truncatedMeta = truncateText(metaInfo, 40);

            return (
                <div
                    key={`left-row-${index}`}
                    className={`gt-row ${isOrder ? 'gt-order-row' : ''} ${isOperation ? 'gt-operation-row' : ''} ${isResource ? 'gt-resource-row' : ''}`}
                    style={{ height: `${DAY_HEIGHT}px` }}
                    title={`${fullName}\n${metaInfo}`}
                >
                    <div className="gt-row-left">
                        <div className="gt-row-content" style={{
                            paddingLeft: isOrder ? '0' : isOperation ? '24px' : '48px'
                        }}>
                            {isOrder && (
                                <div className="gt-expand">
                                    {position.order.operations && position.order.operations.length > 0 && (
                                        <button
                                            className={`gt-expand-btn ${expandedOrders[position.order.id] ? 'gt-expand-btn--expanded' : ''}`}
                                            onClick={(e) => toggleOrderExpand(position.order.id, e)}
                                            aria-label={expandedOrders[position.order.id] ? 'Свернуть' : 'Развернуть'}
                                        >
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                                                <path
                                                    d={expandedOrders[position.order.id] ? "M6 9l6 6 6-6" : "M9 6l6 6-6 6"}
                                                    stroke="currentColor"
                                                    strokeWidth="2"
                                                />
                                            </svg>
                                        </button>
                                    )}
                                </div>
                            )}

                            {isOperation && (
                                <div className="gt-expand">
                                    {position.operation.resources && position.operation.resources.length > 0 && (
                                        <button
                                            className={`gt-expand-btn ${expandedOperations[`${position.order.id}-${position.operation.id}`] ? 'gt-expand-btn--expanded' : ''}`}
                                            onClick={(e) => toggleOperationExpand(`${position.order.id}-${position.operation.id}`, e)}
                                            aria-label={expandedOperations[`${position.order.id}-${position.operation.id}`] ? 'Свернуть' : 'Развернуть'}
                                        >
                                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                                                <path
                                                    d={expandedOperations[`${position.order.id}-${position.operation.id}`] ? "M6 9l6 6 6-6" : "M9 6l6 6-6 6"}
                                                    stroke="currentColor"
                                                    strokeWidth="2"
                                                />
                                            </svg>
                                        </button>
                                    )}
                                </div>
                            )}

                            {isResource && <div className="gt-expand"></div>}

                            <div className="gt-item-info">
                                <div className="gt-item-main">
                                    <div className={`gt-item-icon ${
                                        isOrder ? 'gt-icon-order' :
                                            isOperation ? 'gt-icon-operation' :
                                                'gt-icon-resource'
                                    }`}>
                                        {isOrder ? '📋' : isOperation ? '⚙️' :
                                            position.resource?.type === 'Человек' ? '👤' : '🔧'}
                                    </div>
                                    <div className="gt-item-text">
                                        <div
                                            className="gt-item-title"
                                            title={fullName}
                                        >
                                            {truncatedName}
                                        </div>
                                        <div className="gt-item-meta">
                                            <span
                                                className="gt-meta-item"
                                                data-full={metaInfo}
                                                title={metaInfo}
                                            >
                                                {truncatedMeta}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                                <div
                                    className="gt-status-badge"
                                    style={{
                                        backgroundColor: getStatusColor(itemData.status)
                                    }}
                                    title={statusText}
                                >
                                    {truncatedStatus}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            );
        });
    };

    if (!displayData || displayData.length === 0) {
        return (
            <div className="gt-empty">
                <div className="gt-empty-icon">📊</div>
                <h3 className="gt-empty-title">Нет данных для отображения</h3>
                <p className="gt-empty-text">Заказы не найдены или еще не созданы</p>
            </div>
        );
    }

    return (
        <div className="gt-container">
            {/* Основное тело таблицы с единой горизонтальной прокруткой */}
            <div
                className="gt-body"
                ref={timelineRef}
                style={{
                    overflow: 'auto',
                    height: 'auto',
                    minHeight: '600px'
                }}
            >
                {/* Левая панель - с вертикальной прокруткой */}
                <div
                    className="gt-left-panel"
                    ref={leftPanelRef}
                    style={{
                        overflowY: 'auto',
                        overflowX: 'hidden'
                    }}
                >
                    {/* Заголовок "Заказы" - НЕ фиксированный */}
                    <div className="gt-calendar-placeholder" style={{ height: '80px' }}>
                        <div className="gt-calendar-controls">
                            <div className="gt-data-source-controls">
                                <h3 style={{ margin: 0, fontSize: '16px', color: '#374151' }}>Заказы</h3>
                            </div>
                        </div>
                    </div>

                    {/* Контент левой панели */}
                    <div
                        className="gt-left-content"
                        style={{
                            height: `${timelineContentHeight}px`,
                            overflow: 'hidden'
                        }}
                    >
                        <div className="gt-left-rows-container">
                            {renderLeftPanelRows()}
                        </div>
                    </div>
                </div>

                {/* Правая панель - временная шкала с фиксированным календарем */}
                <div
                    className="gt-timeline-content"
                    style={{
                        width: `${timelineWidth}px`,
                        minWidth: `${timelineWidth}px`,
                        height: '100%',
                        position: 'relative',
                        zIndex: 1,
                        overflow: 'hidden'
                    }}
                >
                    {/* Общий контейнер для правой части */}
                    <div className="gt-timeline-scroll-container">
                        {/* ФИКСИРОВАННЫЙ КАЛЕНДАРЬ */}
                        {renderCalendar()}

                        {/* Контейнер для временных отрезков с вертикальной прокруткой */}
                        <div
                            className="gt-tasks-scroll-container"
                            ref={rightPanelRef}
                            style={{
                                height: `calc(100% - 80px)`,
                                overflowY: 'auto'
                            }}
                        >
                            <div className="gt-tasks-container">
                                {/* Горизонтальный грид строк */}
                                {renderRowGrid()}

                                {/* Вертикальный грид дат */}
                                {renderDateGrid()}

                                {/* Линия сегодняшнего дня */}
                                {renderTodayLine()}

                                {/* Временные отрезки */}
                                <div className="gt-tasks-wrapper" style={{
                                    height: `${timelineContentHeight}px`
                                }}>
                                    {renderTasks()}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default GanttTable;